import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const EditStageModal = ({ apiUrl, open, stageId, onClose, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // form state
  const [mode, setMode] = useState('job'); // 'job' | 'service'
  const [jobNumber, setJobNumber] = useState('');
  const [serviceLogNumber, setServiceLogNumber] = useState('');
  const [stageDate, setStageDate] = useState('');
  const [status, setStatus] = useState('Open');
  const [notes, setNotes] = useState('');

  const canSubmit = useMemo(() => {
    const idOk = Number(stageId) > 0;
    const hasIdentifier = (mode === 'job' && jobNumber.trim().length > 0) ||
                          (mode === 'service' && serviceLogNumber.trim().length > 0);
    return idOk && hasIdentifier && !!stageDate;
  }, [stageId, mode, jobNumber, serviceLogNumber, stageDate]);

  useEffect(() => {
    if (!open || !stageId) return;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${apiUrl}/inventory_crud.php?staging_id=${stageId}`);
        if (Array.isArray(res.data) && res.data[0] === 'success') {
          const h = res.data[1]?.header;
          if (!h) throw new Error('Stage not found');
          // prime form
          const hasJob = !!h.job_number;
          setMode(hasJob ? 'job' : 'service');
          setJobNumber(h.job_number || '');
          setServiceLogNumber(h.service_log_number || '');
          setStageDate(h.stage_date || new Date().toISOString().slice(0,10));
          setStatus(h.status || 'Open');
          setNotes(h.notes || '');
        } else {
          toast.error(res.data?.error || 'Failed to load stage');
        }
      } catch (e) {
        if (e?.response?.status === 401) {
          toast.error('Session expired. Please login again.', { position: 'top-center' });
        } else {
          toast.error('API error loading stage');
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [open, stageId, apiUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    // XOR payload
    const payload = {
      id: Number(stageId),
      job_number: mode === 'job' ? jobNumber.trim() : null,
      service_log_number: mode === 'service' ? serviceLogNumber.trim() : null,
      stage_date: stageDate,
      notes: notes?.trim() || null,
      status, // allow updating status here
    };

    setSubmitting(true);
    try {
      const res = await axios.post(
        `${apiUrl}/inventory_crud.php`,
        { status: 'updateStaging', data: payload },
        { headers: { 'Content-Type': 'application/json' } }
      );

      const ok = (res.data && res.data.success === true)
              || (Array.isArray(res.data) && res.data[0] === 'success');

      if (ok) {
        toast.success('Stage updated');
        onSaved?.();
        onClose();
      } else {
        toast.error(res.data?.error || 'Failed to update stage');
      }
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error('Session expired. Please login again.', { position: 'top-center' });
      } else {
        toast.error('API error updating stage');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white w-full max-w-2xl rounded-lg shadow-lg">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">Edit Stage #{stageId}</h2>
          <button className="text-gray-500 hover:text-gray-700" onClick={onClose}>✕</button>
        </div>

        {loading ? (
          <div className="p-5 text-sm text-gray-500">Loading…</div>
        ) : (
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            {/* Identifier mode */}
            <fieldset className="border rounded p-3">
              <legend className="text-sm font-medium">Identify by</legend>
              <div className="flex items-center gap-6 mt-2">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="mode"
                    value="job"
                    checked={mode === 'job'}
                    onChange={() => { setMode('job'); setServiceLogNumber(''); }}
                  />
                  <span>Job #</span>
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="mode"
                    value="service"
                    checked={mode === 'service'}
                    onChange={() => { setMode('service'); setJobNumber(''); }}
                  />
                  <span>Service Log #</span>
                </label>
              </div>
            </fieldset>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {mode === 'job' ? (
                <div>
                  <label className="block text-sm font-medium mb-1">Job #</label>
                  <input
                    className="w-full border rounded px-3 py-2"
                    value={jobNumber}
                    onChange={(e) => setJobNumber(e.target.value)}
                    placeholder="e.g. J-2025-123"
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium mb-1">Service Log #</label>
                  <input
                    className="w-full border rounded px-3 py-2"
                    value={serviceLogNumber}
                    onChange={(e) => setServiceLogNumber(e.target.value)}
                    placeholder="e.g. SVC-8891"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1">Stage Date</label>
                <input
                  type="date"
                  className="w-full border rounded px-3 py-2"
                  value={stageDate}
                  onChange={(e) => setStageDate(e.target.value)}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Status</label>
                <select
                  className="w-full border rounded px-3 py-2"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="Open">Open</option>
                  <option value="Released">Released</option>
                  <option value="Delivered">Delivered</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Notes</label>
              <textarea
                rows={3}
                className="w-full border rounded px-3 py-2"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button type="button" className="px-4 py-2 rounded border" onClick={onClose} disabled={submitting}>
                Cancel
              </button>
              <button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded disabled:opacity-60"
                disabled={!canSubmit || submitting}
              >
                {submitting ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default EditStageModal;
